<script setup lang="ts">
import { onMounted, computed } from 'vue';
import { useForm } from 'vee-validate';
import { yup } from '@clearbanc/validation';
import { parsePlaceData, PlaceDataOutput } from '@/utils/address';
import {
  stateLabel,
  postalCodeLabel,
  stateOptionsForCountry,
} from '@/utils/local';
import {
  countryOptionsWithPriority,
  isCountryWithPostalCode,
} from '@/data/country-code-with-names';
import { COUNTRY_CODES } from '@/data/supported-country-codes';
import { CountryCodeEntry } from '@/types/address.types';
import { UserResponseDto } from '@clearbanc/baus-sdk';
import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';
import DropdownGroup from '@/components/forms/groups/DropdownGroup.vue';
import AutoCompleteAddress from '@/components/AutoCompleteAddress.vue';

interface FormValues {
  line1?: string;
  line2?: string;
  city?: string;
  state?: CountryCodeEntry | string;
  country?: CountryCodeEntry | string;
  postalCode?: string;
}

const props = defineProps<{ user: UserResponseDto }>();

// ---------
// Variables
// ---------

const COUNTRIES = countryOptionsWithPriority([
  COUNTRY_CODES.US,
  COUNTRY_CODES.CA,
  COUNTRY_CODES.GB,
]);

// ----------
// Form Logic
// ----------

const validationSchema = yup.object({
  line1: yup.address(),
  line2: yup.address().notRequired(),
  city: yup.city(),
  state: yup.state(),
  country: yup.country(),
  postalCode: yup.postalCode(),
});

const { values, setValues, validate } = useForm<FormValues>({
  validationSchema,
});

const countryValue = computed(() => {
  if (typeof values.country === 'string') return values.country;
  return values.country?.value;
});

const stateValue = computed(() => {
  if (typeof values.state === 'string') return values.state;
  return values.state?.value;
});

async function validateAndGetValues() {
  const { valid } = await validate();
  if (valid) {
    return {
      address: {
        line1: values.line1,
        line2: values.line2,
        city: values.city,
        state: stateValue.value,
        country: countryValue.value,
        postalCode: values.postalCode,
      },
    };
  }
  return undefined;
}

defineExpose({
  validateAndGetValues,
});

function handleAddressSelected(placeData: PlaceDataOutput) {
  const parsedData = parsePlaceData(placeData);

  setValues({
    ...values,
    ...parsedData,
  } as FormValues);
}

function populateSavedValues() {
  const userAddress = props.user?.address;

  if (!userAddress) return;

  const initialValues: FormValues = {};

  // Address fields
  if (userAddress.line1) initialValues.line1 = userAddress.line1;
  if (userAddress.line2) initialValues.line2 = userAddress.line2;
  if (userAddress.city) initialValues.city = userAddress.city;
  if (userAddress.postalCode) initialValues.postalCode = userAddress.postalCode;

  // State (needs to be an object for dropdown)
  if (userAddress.state && userAddress.country) {
    const stateOption = stateOptionsForCountry(userAddress.country)?.find(
      (state) => state.value === userAddress.state,
    );
    if (stateOption) {
      initialValues.state = stateOption;
    }
  }

  // Country (needs to be an object for dropdown)
  if (userAddress.country) {
    const countryOption = COUNTRIES.find(
      (country) => country.value === userAddress.country,
    );
    if (countryOption) {
      initialValues.country = countryOption;
    }
  }

  setValues(initialValues);
}

// -----------------
// Lifecycle Methods
// -----------------

onMounted(() => {
  populateSavedValues();
});
</script>

<template>
  <div class="c-group">
    <div class="c-group__header">
      <h3 class="c-group__header__title">Address</h3>
    </div>

    <div class="c-form__row">
      <AutoCompleteAddress
        name="line1"
        label="Home Address"
        @addressSelected="handleAddressSelected"
      />
    </div>

    <div class="c-form__row">
      <InputTextGroup name="line2" label="Home Address Line 2" />
    </div>

    <div class="c-form__row grid grid-cols-12">
      <div class="col-span-6 md:col-span-12">
        <DropdownGroup
          name="country"
          label="Country"
          :reset-filter-on-hide="true"
          :filter="true"
          option-label="nameString"
          :options="COUNTRIES"
          placeholder="Select"
          append-to="self"
        />
      </div>
      <div class="col-span-6 md:col-span-12">
        <InputTextGroup name="city" label="City" />
      </div>
    </div>

    <div
      v-if="
        stateOptionsForCountry(countryValue) ||
        (countryValue && isCountryWithPostalCode(countryValue))
      "
      class="c-form__row grid grid-cols-12"
    >
      <div
        v-if="stateOptionsForCountry(countryValue)"
        class="col-span-6 md:col-span-12"
      >
        <DropdownGroup
          name="state"
          :label="stateLabel(countryValue)"
          :filter="true"
          :reset-filter-on-hide="true"
          option-label="nameString"
          :options="stateOptionsForCountry(countryValue)"
          placeholder="Select"
          append-to="self"
        />
      </div>

      <div class="col-span-6 md:col-span-12">
        <InputTextGroup
          v-if="countryValue && isCountryWithPostalCode(countryValue)"
          name="postalCode"
          :label="postalCodeLabel(countryValue)"
        />
      </div>
    </div>
  </div>
</template>
